import type { Plaid } from "plaid-link";
import * as Sentry from "@sentry/react";
import {
    CreatePlaidConnectionResponse,
    CreatePlaidConnectionResponseDto,
} from "@common/dto/plaid/create-plaid-connection-response.dto";
import { CreateBankAccountConnectionDto } from "@common/dto/onboarding/create-bank-account-connection.dto";
import { PlaidConnection } from "@common/types/plaidConnection";
import { PlaidConnectionDto } from "@common/dto/plaidConnection/plaid-connection.dto";
import { plaidConnectionContract } from "@common/contracts/plaid-connection.contract";
import { relinkSuccessResponseSchema } from "@common/schemas/plaid-connection.schema";
import type { RelinkSuccessResponse } from "@common/schemas/plaid-connection.schema";
import { PLAID_INSTITUTIONS_NAMES } from "@common/constants";
import { getBackendAPIClient } from "./backendAPIClient";
import { getAnalytics } from "./analytics";

const plaidConnectionApiClient = getBackendAPIClient(plaidConnectionContract);

type PlaidRelinkResult = RelinkSuccessResponse;

interface PlaidSuccessfulResponse {
    connected: true;
    publicToken: string;
    metadata: Plaid.OnSuccessMetaData;
    linkSessionId?: string;
    relinkResult?: PlaidRelinkResult;
}

interface PlaidFailedResponse {
    connected: false;
    publicToken?: never;
    metadata?: never;
    linkSessionId?: never;
    relinkResult?: never;
}

interface ConnectWithPlaidOptions {
    connectionId?: number;
    addAccounts?: boolean;
    enableStatements?: boolean;
    requireRelinkSuccess?: boolean;
    institutionName?: PLAID_INSTITUTIONS_NAMES;
    workspaceId?: string;
    organizationId?: string;
}
type PlaidResponse = PlaidSuccessfulResponse | PlaidFailedResponse;

type PlaidLinkErrorCaptureSource = "exitError" | "exitAfterInlineError";

interface PlaidLinkErrorContext {
    linkSessionId?: string;
    institutionId?: string | null;
    institutionName?: string | null;
    requestId?: string | null;
    status?: string | null;
    viewName?: string | null;
    errorCode?: string | null;
    errorType?: string | null;
    errorMessage?: string | null;
    isRelink: boolean;
    connectionId?: number;
    addAccounts?: boolean;
}

interface PendingPlaidLinkError {
    errorCode?: string | null;
    errorType?: string | null;
    errorMessage?: string | null;
    viewName?: string | null;
    linkSessionId?: string | null;
    institutionId?: string | null;
    institutionName?: string | null;
    requestId?: string | null;
}

function capturePlaidLinkError(
    source: PlaidLinkErrorCaptureSource,
    context: PlaidLinkErrorContext,
): void {
    const errorCode = context.errorCode ?? "unknown";

    Sentry.captureMessage(`Plaid Link ERROR event: ${errorCode}`, (scope) =>
        scope
            .setLevel("warning")
            .setTag("plaidLink", source)
            .setTag("plaidErrorCode", errorCode)
            .setTag("plaidErrorType", context.errorType ?? "unknown")
            .setContext("plaidLink", {
                linkSessionId: context.linkSessionId,
                institutionId: context.institutionId,
                institutionName: context.institutionName,
                requestId: context.requestId,
                status: context.status,
                viewName: context.viewName,
                errorCode: context.errorCode,
                errorType: context.errorType,
                errorMessage: context.errorMessage,
                isRelink: context.isRelink,
                connectionId: context.connectionId,
                addAccounts: context.addAccounts,
            }),
    );
}

export async function connectWithPlaid(
    options?: ConnectWithPlaidOptions,
): Promise<PlaidResponse> {
    let sessionIdTracked = false;
    let capturedLinkSessionId: string | undefined;
    let hasCapturedLinkError = false;
    let pendingLinkError: PendingPlaidLinkError | undefined;
    const { token } = await getPlaidLinkToken(options ?? {});

    const response = await new Promise<PlaidResponse>((resolve, reject) => {
        window.Plaid?.create({
            token,
            onSuccess: (publicToken, metadata) =>
                resolve({
                    connected: true,
                    publicToken,
                    metadata,
                    linkSessionId: capturedLinkSessionId,
                }),
            onExit: (error, metadata) => {
                if (error) {
                    if (!hasCapturedLinkError) {
                        capturePlaidLinkError("exitError", {
                            linkSessionId:
                                metadata?.link_session_id ??
                                capturedLinkSessionId,
                            institutionId:
                                metadata?.institution?.institution_id,
                            institutionName: metadata?.institution?.name,
                            requestId: metadata?.request_id,
                            status: metadata?.status,
                            errorCode: error.error_code,
                            errorType: error.error_type,
                            errorMessage: error.error_message,
                            isRelink: Boolean(options?.connectionId),
                            connectionId: options?.connectionId,
                            addAccounts: Boolean(options?.addAccounts),
                        });
                        hasCapturedLinkError = true;
                    }

                    reject(
                        new Error(
                            `Plaid error ${error.error_code}: ${error.error_message}`,
                        ),
                    );
                    return;
                }

                if (pendingLinkError && !hasCapturedLinkError) {
                    capturePlaidLinkError("exitAfterInlineError", {
                        linkSessionId:
                            metadata?.link_session_id ??
                            pendingLinkError.linkSessionId ??
                            capturedLinkSessionId,
                        institutionId:
                            metadata?.institution?.institution_id ??
                            pendingLinkError.institutionId,
                        institutionName:
                            metadata?.institution?.name ??
                            pendingLinkError.institutionName,
                        requestId:
                            metadata?.request_id ?? pendingLinkError.requestId,
                        status: metadata?.status,
                        viewName: pendingLinkError.viewName,
                        errorCode: pendingLinkError.errorCode,
                        errorType: pendingLinkError.errorType,
                        errorMessage: pendingLinkError.errorMessage,
                        isRelink: Boolean(options?.connectionId),
                        connectionId: options?.connectionId,
                        addAccounts: Boolean(options?.addAccounts),
                    });
                    hasCapturedLinkError = true;
                }

                resolve({ connected: false });
            },
            onEvent: (eventName, metadata) => {
                if (!sessionIdTracked) {
                    capturedLinkSessionId = metadata.link_session_id;
                    getAnalytics()?.track("Plaid Link Session", {
                        linkSessionId: metadata.link_session_id,
                    });
                    sessionIdTracked = true;
                }

                if (eventName === "ERROR") {
                    pendingLinkError = {
                        errorCode: metadata.error_code,
                        errorType: metadata.error_type,
                        errorMessage: metadata.error_message,
                        viewName: metadata.view_name,
                        linkSessionId:
                            metadata.link_session_id ?? capturedLinkSessionId,
                        institutionId: metadata.institution_id,
                        institutionName: metadata.institution_name,
                        requestId: metadata.request_id,
                    };
                }
            },
        }).open();
    });

    if (response.connected && options?.connectionId) {
        try {
            const relinkResult = await notifyRelinkSuccess(
                options.connectionId,
                response.linkSessionId,
            );

            if (!relinkResult) {
                const error = new Error("Invalid relink success response");
                if (options.requireRelinkSuccess) {
                    throw error;
                }
                return response;
            }

            return { ...response, relinkResult };
        } catch (error) {
            const isInvalidRelinkBody =
                error instanceof Error &&
                error.message === "Invalid relink success response";

            if (!isInvalidRelinkBody) {
                Sentry.captureException(error, (scope) =>
                    scope.setContext("plaidRelink", {
                        connectionId: options.connectionId,
                    }),
                );
            }
            if (options.requireRelinkSuccess) {
                throw error;
            }
        }
    }

    return response;
}

async function getPlaidLinkToken({
    connectionId,
    addAccounts,
    institutionName,
    enableStatements,
    workspaceId,
    organizationId,
}: ConnectWithPlaidOptions): Promise<{ token: string }> {
    if (connectionId) {
        const { body } =
            await plaidConnectionApiClient.getLinkTokenForExistingConnection({
                params: { connectionId },
                query: {
                    addAccounts: addAccounts ? "true" : "false",
                    enableStatements: enableStatements ? "true" : "false",
                    workspaceId,
                    organizationId,
                },
            });
        return body;
    }

    const { body } =
        await plaidConnectionApiClient.getLinkTokenForNewConnection({
            query: { institutionName, workspaceId, organizationId },
        });
    return body;
}

export async function connectPlaidConnection(
    params: CreateBankAccountConnectionDto,
): Promise<CreatePlaidConnectionResponse> {
    const { body } = await plaidConnectionApiClient.addConnection({
        body: params,
    });
    return CreatePlaidConnectionResponseDto.deserialize(body);
}

export async function getPlaidConnections(institutionId?: string) {
    const { body } = await plaidConnectionApiClient.getConnections({
        query: { institutionId },
    });

    return body.map((dto: PlaidConnectionDto) =>
        PlaidConnectionDto.deserialize(dto),
    );
}

export async function connectNewPlaidAccounts(connection: PlaidConnection) {
    const { body } = await plaidConnectionApiClient.addNewAccounts({
        params: { connectionId: connection.id },
    });
    return {
        connection: PlaidConnectionDto.deserialize(body.connection),
        foreign: body.foreign,
    };
}

async function notifyRelinkSuccess(
    connectionId: number,
    linkSessionId?: string,
): Promise<PlaidRelinkResult | undefined> {
    const { body } = await plaidConnectionApiClient.onRelinkSuccess({
        params: { connectionId },
        body: { linkSessionId },
    });
    const parsed = relinkSuccessResponseSchema.safeParse(body);
    if (!parsed.success) {
        return undefined;
    }
    return parsed.data;
}

export async function deletePlaidConnection(
    connectionId: number,
): Promise<void> {
    await plaidConnectionApiClient.deleteConnection({
        params: { connectionId },
    });
}
